/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ObjectExtensions } from './object-extensions';

/**
 * Represents an array which is defined with at least one value.
 */
export type NonEmptyArray<T> = [T, ...T[]];

/**
 * Namespace which extends Array functionality.
 */
export namespace ArrayExtensions {

    /**
     * Verifies whether the object array has elements.
     * @param value - The array to examine.
     * @returns True if the object has at least one element in the array, false otherwise (if array is not defined or empty).
     */
    export function hasElements<T>(value: NonEmptyArray<T> | T[] | null | undefined): value is NonEmptyArray<T> {
        const emptyArrayLength = 0;
        return !ObjectExtensions.isNullOrUndefined(value) && value.length > emptyArrayLength;
    }

    /**
     * Remove duplicate elements in the array.
     * @param value - The array to examine.
     * @returns An array with only unique elements.
     */
    export function unique<T>(value: T[]): T[] {
        if (!hasElements(value)) {
            return [];
        }

        return Array.from(new Set(value));
    }

    /**
     * Checks if all elements satisfy given condition.
     * @param value - The array to examine.
     * @param predicate - The function which checks an element.
     * @returns True if the array is empty or all the inner elements satisfy given condition,
     * false if at least one element does not pass the condition.
     */
    export function all<T>(value: T[] | null | undefined, predicate: (value: T, index: number, array: T[]) => boolean): boolean {
        if (!hasElements(value)) {
            return true;
        }

        return value.filter(predicate).length === value.length;
    }

    /**
     * Filters out null and undefined values from the given array.
     * @param value - The array with possibly undefined/null items.
     * @returns All items which are not null or undefined.
     */
    export function validValues<T>(value: (T | undefined | null)[] | undefined | null): T[] {
        if (!hasElements(value)) {
            return [];
        }

        return value.filter(item => !ObjectExtensions.isNullOrUndefined(item)) as T[];
    }

    /**
     * Merges items from 2-dimensional array into one array with all items.
     * @param value - The 2-dimensional array with the items.
     * @returns All items retrieved from all inner arrays.
     */
    export function flatten<T>(value: ((T | undefined | null)[] | undefined | null)[]): T[] {
        let result: T[] = [];

        if (!hasElements(value)) {
            return result;
        }

        for (const arrayLine of value) {
            if (!ObjectExtensions.isNullOrUndefined(arrayLine)) {
                result = result.concat(validValues(arrayLine));
            }
        }

        return result;
    }

    /**
     * Check if two arrays are equal.
     * @param firstArray - The array with the items.
     * @param secondArray - Another array with the items.
     * @param comparator - The comparator which compares the values and returns true if two objects are equal, false otherwise.
     * @returns True if arrays have the same items, false otherwise.
     */
    export function equals<T>(firstArray: T[], secondArray: T[], comparator?: (valueFromFirstArray: T, valueFromSecondArray: T) => boolean): boolean {
        if (firstArray.length !== secondArray.length) {
            return false;
        }

        if (comparator === undefined) {
            return JSON.stringify(firstArray) === JSON.stringify(secondArray);
        }

        // eslint-disable-next-line unicorn/no-for-loop -- Disable as we need to compare by indices.
        for (let index = 0; index < firstArray.length; ++index) {
            if (!comparator(firstArray[index], secondArray[index])) {
                return false;
            }
        }

        return true;
    }
}
